import { useState } from "react";
import {
    Box,
    TableCell,
    TableRow,
    Collapse,
    IconButton,
    Typography,
    Chip,
    Grid,
    Divider,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    CircularProgress,
    Alert,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { RestaurantSubmission, RestaurantSubmissionStatus } from '../../../../../models/DB';
import { RestaurantSubmissionService } from '../../../../../services/RestaurantSubmissionService';
import { useStoreConfig } from '../../../../../contexts/StoreConfigContext';

interface Props {
    submission: RestaurantSubmission;
    onUpdate: () => void;
    isMobile: boolean;
}

const statusColors: Record<RestaurantSubmissionStatus, 'warning' | 'success' | 'error' | 'info'> = {
    pending: 'warning',
    approved: 'success',
    rejected: 'error',
    contacted: 'info',
};

const businessTypeLabels: Record<string, string> = {
    PTY_LTD: 'PTY (LTD)',
    LTD: 'LTD',
    CC: 'CC',
    SOLE_OWNER: 'Sole Owner',
    CO_OP: 'Co-Op',
    P_SHIP: 'Partnership',
    OTHER: 'Other',
};

const paymentTermsLabels: Record<string, string> = {
    in_advance: 'In Advance',
    cod: 'C.O.D.',
    '7_days': '7 Days',
    '14_days': '14 Days',
};

export const RestaurantSubmissionRow = ({ submission, onUpdate, isMobile }: Props) => {
    const theme = useTheme();
    const { config } = useStoreConfig();
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState<RestaurantSubmissionStatus>(submission.status);
    const [adminNotes, setAdminNotes] = useState(submission.admin_notes || '');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-ZA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const result = await RestaurantSubmissionService.updateStatus(
                submission.id,
                status,
                adminNotes
            );
            if (result) {
                onUpdate();
            } else {
                setError('Failed to save changes');
            }
        } catch (err) {
            setError('An error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this submission?')) {
            return;
        }

        setDeleting(true);
        setError(null);
        try {
            const success = await RestaurantSubmissionService.deleteSubmission(submission.id);
            if (success) {
                onUpdate();
            } else {
                setError('Failed to delete submission');
            }
        } catch (err) {
            setError('An error occurred');
        } finally {
            setDeleting(false);
        }
    };

    const hasChanges = status !== submission.status || adminNotes !== (submission.admin_notes || '');

    const DetailSection = ({
        title,
        icon,
        children,
    }: {
        title: string;
        icon: React.ReactNode;
        children: React.ReactNode;
    }) => (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: `${config.primaryColor}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: config.primaryColor,
                    }}
                >
                    {icon}
                </Box>
                <Typography variant='subtitle1' fontWeight={600}>
                    {title}
                </Typography>
            </Box>
            {children}
        </Box>
    );

    const DetailField = ({ label, value }: { label: string; value?: string | boolean | null }) => {
        if (value === undefined || value === null || value === '') return null;
        return (
            <Box sx={{ mb: 1 }}>
                <Typography variant='caption' color='text.secondary'>
                    {label}
                </Typography>
                <Typography variant='body2'>
                    {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                </Typography>
            </Box>
        );
    };

    return (
        <>
            <TableRow
                hover
                sx={{
                    '& > *': { borderBottom: 'unset' },
                    cursor: 'pointer',
                    backgroundColor: open ? theme.palette.action.hover : 'inherit',
                }}
                onClick={() => setOpen(!open)}
            >
                <TableCell>
                    <IconButton size='small'>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Typography variant='body2'>{formatDate(submission.created)}</Typography>
                </TableCell>
                <TableCell>
                    <Box>
                        <Typography variant='body2' fontWeight={500}>
                            {submission.registered_name}
                        </Typography>
                        {submission.trading_name && (
                            <Typography variant='caption' color='text.secondary'>
                                t/a {submission.trading_name}
                            </Typography>
                        )}
                    </Box>
                </TableCell>
                {!isMobile && (
                    <>
                        <TableCell>
                            <Typography variant='body2'>
                                {businessTypeLabels[submission.business_type] || submission.business_type}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Box>
                                <Typography variant='body2'>{submission.mobile}</Typography>
                                {submission.accounts_email && (
                                    <Typography variant='caption' color='text.secondary'>
                                        {submission.accounts_email}
                                    </Typography>
                                )}
                            </Box>
                        </TableCell>
                    </>
                )}
                <TableCell>
                    <Chip
                        label={submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                        color={statusColors[submission.status]}
                        size='small'
                    />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={isMobile ? 4 : 6}>
                    <Collapse in={open} timeout='auto' unmountOnExit>
                        <Box sx={{ py: 3, px: 2 }}>
                            {error && (
                                <Alert severity='error' sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <Grid container spacing={3}>
                                {/* Status & Notes */}
                                <Grid item xs={12} md={4}>
                                    <DetailSection
                                        title='Status & Notes'
                                        icon={<BusinessIcon sx={{ fontSize: 18 }} />}
                                    >
                                        <FormControl fullWidth size='small' sx={{ mb: 2 }}>
                                            <InputLabel>Status</InputLabel>
                                            <Select
                                                value={status}
                                                label='Status'
                                                onChange={(e) =>
                                                    setStatus(e.target.value as RestaurantSubmissionStatus)
                                                }
                                            >
                                                <MenuItem value='pending'>Pending</MenuItem>
                                                <MenuItem value='contacted'>Contacted</MenuItem>
                                                <MenuItem value='approved'>Approved</MenuItem>
                                                <MenuItem value='rejected'>Rejected</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            label='Admin Notes'
                                            multiline
                                            rows={3}
                                            fullWidth
                                            size='small'
                                            value={adminNotes}
                                            onChange={(e) => setAdminNotes(e.target.value)}
                                        />
                                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                            <Button
                                                variant='contained'
                                                size='small'
                                                startIcon={
                                                    saving ? (
                                                        <CircularProgress size={16} color='inherit' />
                                                    ) : (
                                                        <SaveIcon />
                                                    )
                                                }
                                                disabled={!hasChanges || saving || deleting}
                                                onClick={handleSave}
                                                sx={{
                                                    backgroundColor: config.primaryColor,
                                                    '&:hover': {
                                                        backgroundColor: config.primaryColor,
                                                        filter: 'brightness(0.9)',
                                                    },
                                                }}
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                variant='outlined'
                                                size='small'
                                                color='error'
                                                startIcon={
                                                    deleting ? (
                                                        <CircularProgress size={16} color='inherit' />
                                                    ) : (
                                                        <DeleteIcon />
                                                    )
                                                }
                                                disabled={saving || deleting}
                                                onClick={handleDelete}
                                            >
                                                Delete
                                            </Button>
                                        </Box>
                                    </DetailSection>
                                </Grid>

                                {/* Business Information */}
                                <Grid item xs={12} md={4}>
                                    <DetailSection
                                        title='Business Information'
                                        icon={<BusinessIcon sx={{ fontSize: 18 }} />}
                                    >
                                        <DetailField
                                            label='Business Type'
                                            value={businessTypeLabels[submission.business_type]}
                                        />
                                        <DetailField label='Registered Name' value={submission.registered_name} />
                                        <DetailField label='Trading Name' value={submission.trading_name} />
                                        <DetailField label='Registration Number' value={submission.registration_number} />
                                        <DetailField label='VAT Number' value={submission.vat_number} />
                                        <DetailField
                                            label='Payment Terms'
                                            value={
                                                submission.payment_terms
                                                    ? paymentTermsLabels[submission.payment_terms]
                                                    : undefined
                                            }
                                        />
                                        <DetailField
                                            label='Accept Back Orders'
                                            value={submission.accept_back_orders}
                                        />
                                    </DetailSection>
                                </Grid>

                                {/* Contact Information */}
                                <Grid item xs={12} md={4}>
                                    <DetailSection
                                        title='Contact Information'
                                        icon={<PhoneIcon sx={{ fontSize: 18 }} />}
                                    >
                                        <DetailField label='Mobile' value={submission.mobile} />
                                        <DetailField label='Telephone' value={submission.telephone} />
                                        <DetailField label='Principal Address' value={submission.principal_address} />
                                        <DetailField label='Delivery Address' value={submission.delivery_address} />
                                        <Divider sx={{ my: 1.5 }} />
                                        <Typography variant='caption' color='text.secondary' fontWeight={600}>
                                            Accounts Department
                                        </Typography>
                                        <DetailField label='Contact' value={submission.accounts_contact_name} />
                                        <DetailField label='Email' value={submission.accounts_email} />
                                        <Divider sx={{ my: 1.5 }} />
                                        <Typography variant='caption' color='text.secondary' fontWeight={600}>
                                            Buying Department
                                        </Typography>
                                        <DetailField label='Contact' value={submission.buying_contact_name} />
                                        <DetailField label='Email' value={submission.buying_email} />
                                    </DetailSection>
                                </Grid>

                                {/* Owner Information */}
                                {(submission.owner1_full_names || submission.owner2_full_names) && (
                                    <Grid item xs={12} md={6}>
                                        <DetailSection
                                            title='Owner / Director Information'
                                            icon={<PersonIcon sx={{ fontSize: 18 }} />}
                                        >
                                            {submission.owner1_full_names && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography
                                                        variant='caption'
                                                        color='text.secondary'
                                                        fontWeight={600}
                                                    >
                                                        Owner 1
                                                    </Typography>
                                                    <DetailField label='Full Names' value={submission.owner1_full_names} />
                                                    <DetailField label='ID Number' value={submission.owner1_id_number} />
                                                    <DetailField label='Address' value={submission.owner1_address} />
                                                    <DetailField label='Mobile' value={submission.owner1_mobile} />
                                                </Box>
                                            )}
                                            {submission.owner2_full_names && (
                                                <Box>
                                                    <Typography
                                                        variant='caption'
                                                        color='text.secondary'
                                                        fontWeight={600}
                                                    >
                                                        Owner 2
                                                    </Typography>
                                                    <DetailField label='Full Names' value={submission.owner2_full_names} />
                                                    <DetailField label='ID Number' value={submission.owner2_id_number} />
                                                    <DetailField label='Address' value={submission.owner2_address} />
                                                    <DetailField label='Mobile' value={submission.owner2_mobile} />
                                                </Box>
                                            )}
                                        </DetailSection>
                                    </Grid>
                                )}

                                {/* Banking Information */}
                                {submission.bank_name && (
                                    <Grid item xs={12} md={6}>
                                        <DetailSection
                                            title='Banking Details'
                                            icon={<AccountBalanceIcon sx={{ fontSize: 18 }} />}
                                        >
                                            <DetailField label='Bank Name' value={submission.bank_name} />
                                            <DetailField label='Branch Name' value={submission.branch_name} />
                                            <DetailField label='Branch Code' value={submission.branch_code} />
                                            <DetailField label='Account Number' value={submission.account_number} />
                                        </DetailSection>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

export default RestaurantSubmissionRow;
